import { useCallback, useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { AlertTriangle } from "lucide-react";

// Guard anti-duplicazione targhe centralizzato.
// Uso: import { guardTarga } from ".../context/TargaGuard"
//   const { action, match } = await guardTarga(targa, clienteId, praticaId)
//   action: "ok" (nessun duplicato / avviso soppresso) | "existing" (usa soggetto esistente) | "continue"
let _impl = null;

const normTarga = (s) => String(s || "").toUpperCase().replace(/[^A-Z0-9]/g, "");

// Soppressione avviso per singola pratica (persistente in localStorage).
const SUPPRESS_KEY = "pgp_targa_suppress";
const isSuppressed = (pid) => {
  if (!pid) return false;
  try { return JSON.parse(localStorage.getItem(SUPPRESS_KEY) || "[]").includes(String(pid)); } catch { return false; }
};
const addSuppressed = (pid) => {
  if (!pid) return;
  try {
    const a = JSON.parse(localStorage.getItem(SUPPRESS_KEY) || "[]");
    if (!a.includes(String(pid))) { a.push(String(pid)); localStorage.setItem(SUPPRESS_KEY, JSON.stringify(a)); }
  } catch { /* ignore */ }
};

export function guardTarga(targa, clienteId, praticaId) {
  const t = normTarga(targa);
  if (!t || !_impl) return Promise.resolve({ action: "ok", match: null });
  return _impl(targa, clienteId, praticaId);
}

export function TargaGuardProvider({ children }) {
  const [state, setState] = useState(null); // { targa, match, praticaId }
  const [dontShow, setDontShow] = useState(false);
  const resolver = useRef(null);

  const impl = useCallback((targa, clienteId, praticaId) => new Promise((resolve) => {
    (async () => {
      // Avviso soppresso per questa pratica: prosegui senza mostrare la modale.
      if (isSuppressed(praticaId)) { resolve({ action: "ok", match: null }); return; }
      try {
        const params = new URLSearchParams({ targa: String(targa || "") });
        if (clienteId) params.set("cliente_id", String(clienteId));
        if (praticaId) params.set("pratica_id", String(praticaId));
        const { data } = await api.get(`/studio/targa-check?${params.toString()}`);
        const match = data && data.match;
        if (!match) { resolve({ action: "ok", match: null }); return; }
        resolver.current = (choice) => {
          resolver.current = null;
          setState(null);
          resolve({ action: choice, match });
        };
        setDontShow(false);
        setState({ targa: normTarga(targa), match, praticaId: praticaId || null });
      } catch {
        resolve({ action: "ok", match: null });
      }
    })();
  }), []);

  useEffect(() => { _impl = impl; return () => { if (_impl === impl) _impl = null; }; }, [impl]);

  const decide = (choice) => {
    if (choice === "continue" && dontShow && state?.praticaId) addSuppressed(state.praticaId);
    const r = resolver.current;
    if (r) r(choice);
  };

  const pratiche = state?.match?.pratiche || [];

  return (
    <>
      {children}
      <Dialog open={!!state} onOpenChange={(o) => { if (!o) decide("continue"); }}>
        <DialogContent className="bg-[#121824] border-slate-700 text-slate-100 max-w-md" data-testid="targa-guard-dialog">
          <DialogHeader>
            <DialogTitle className="font-head flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-400" /> Targa già presente
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-200 leading-relaxed" data-testid="targa-guard-message">
            Attenzione: la targa <span className="font-mono font-semibold text-amber-300">{state?.targa}</span> è già
            associata a <span className="font-semibold">{state?.match?.display || "—"}</span>
            {pratiche.length > 0 ? (
              <> nelle pratiche: <span className="font-mono text-amber-300" data-testid="targa-guard-pratiche">{pratiche.join(", ")}</span></>
            ) : null}
            . Vuoi continuare comunque o usare il soggetto esistente?
          </p>
          <label className="flex items-center gap-2 mt-3 cursor-pointer select-none">
            <Checkbox checked={dontShow} onCheckedChange={(v) => setDontShow(!!v)} data-testid="targa-guard-dontshow" />
            <span className="text-xs text-slate-400">Non mostrare più questo avviso per questa pratica</span>
          </label>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => decide("existing")} data-testid="targa-guard-existing"
              className="border-slate-600 text-slate-100 hover:bg-slate-800">Usa soggetto esistente</Button>
            <Button onClick={() => decide("continue")} data-testid="targa-guard-continue"
              className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">Continua comunque</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
